import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import DocumentManager from '../components/DocumentManager';
import { formatCurrency } from '../utils/currency';
import './CustomerDetail.css';
import { toast } from '../components/Toast';

function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);


  useEffect(() => {
    fetchCustomerDetails();
    fetchProjects();
    fetchPayments();
  }, [id]);

  const fetchCustomerDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/customers/${id}`);
      const customerData = response.data.data || response.data;
      setCustomer(customerData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`/customers/${id}/projects`);
      const projectData = response.data.data || response.data;
      setProjects(projectData);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`/customers/${id}/payments`);
      const paymentData = response.data.data || response.data;
      setPayments(paymentData);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };







  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      PLANNED: 'badge-planned',
      ACTIVE: 'badge-active',
      DELAYED: 'badge-delayed',
      COMPLETED: 'badge-completed',
      CANCELLED: 'badge-cancelled'
    };
    return statusClasses[status] || '';
  };

  if (loading) {
    return <div className="loading">Loading customer details...</div>;
  }

  if (!customer) {
    return (
      <div className="customer-detail-container">
        <p style={{ margin: '20px 0' }}>Customer not found.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/customers')}>
          Back to Customers
        </button>
      </div>
    );
  }

  return (
    <div className="customer-detail-container">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
            ← Back to Customers
          </button>
          <h1>Customer Details</h1>
        </div>

      </div>

      <div className="customer-info-card">
        <div className="card-header">
          <h2>Customer Information</h2>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <label>Customer ID</label>
            <span>{customer.id}</span>
          </div>
          <div className="info-item">
            <label>Name</label>
            <span>{customer.name}</span>
          </div>
          <div className="info-item">
            <label>Phone</label>
            <span>{customer.phone}</span>
          </div>
          <div className="info-item">
            <label>Email</label>
            <span>{customer.email || '-'}</span>
          </div>
          <div className="info-item">
            <label>Total Paid</label>
            <span className="amount-paid">{formatCurrency(customer.totalPaid || 0)}</span>
          </div>
          <div className="info-item">
            <label>Total Pending</label>
            <span className="amount-pending">{formatCurrency(customer.totalPending || 0)}</span>
          </div>
          <div className="info-item">
            <label>Created At</label>
            <span>{new Date(customer.createdAt).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <label>Last Updated</label>
            <span>{new Date(customer.updatedAt).toLocaleString()}</span>
          </div>
          {customer.address && (
            <div className="info-item full-width">
              <label>Address</label>
              <p>{customer.address}</p>
            </div>
          )}
        </div>
      </div>

      {/* Graphical Representation */}
      <div className="customer-visual-summary">
        <div className="card-header">
          <h2>Financial Overview</h2>
        </div>
        
        <div className="visual-grid">
          {/* Budget vs Paid Chart */}
          <div className="visual-card">
            <h3>Budget vs Paid</h3>
            <div className="chart-container">
              <div className="chart-bar-container">
                <div className="chart-bar-label">Total Budget</div>
                <div className="chart-bar-bg">
                  <div 
                    className="chart-bar-fill budget"
                    style={{ width: '100%' }}
                  ></div>
                </div>
                <div className="chart-bar-value">{formatCurrency(customer.totalBudget || 0)}</div>
              </div>
              
              <div className="chart-bar-container">
                <div className="chart-bar-label">Paid</div>
                <div className="chart-bar-bg">
                  <div 
                    className="chart-bar-fill paid"
                    style={{ 
                      width: customer.totalBudget && customer.totalBudget > 0 
                        ? Math.min((customer.totalPaid / customer.totalBudget) * 100, 100) 
                        : 0 
                    }}
                  ></div>
                </div>
                <div className="chart-bar-value">{formatCurrency(customer.totalPaid || 0)}</div>
              </div>
              
              <div className="chart-bar-container">
                <div className="chart-bar-label">Pending</div>
                <div className="chart-bar-bg">
                  <div 
                    className="chart-bar-fill pending"
                    style={{ 
                      width: customer.totalBudget && customer.totalBudget > 0 
                        ? Math.min((customer.totalPending / customer.totalBudget) * 100, 100) 
                        : 0 
                    }}
                  ></div>
                </div>
                <div className="chart-bar-value">{formatCurrency(customer.totalPending || 0)}</div>
              </div>
            </div>
          </div>

          {/* Project Status Distribution */}
          <div className="visual-card">
            <h3>Project Status</h3>
            <div className="status-distribution">
              {projects.length > 0 ? (
                <>
                  <div className="status-item">
                    <span className="status-badge planned"></span>
                    <span>Planned: {projects.filter(p => p.status === 'PLANNED').length}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge active"></span>
                    <span>Active: {projects.filter(p => p.status === 'ACTIVE').length}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge delayed"></span>
                    <span>Delayed: {projects.filter(p => p.status === 'DELAYED').length}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge completed"></span>
                    <span>Completed: {projects.filter(p => p.status === 'COMPLETED').length}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-badge cancelled"></span>
                    <span>Cancelled: {projects.filter(p => p.status === 'CANCELLED').length}</span>
                  </div>
                </>
              ) : (
                <div className="no-data">No projects yet</div>
              )}
            </div>
          </div>

          {/* Payment Progress */}
          <div className="visual-card">
            <h3>Payment Progress</h3>
            <div className="progress-circle">
              {customer.totalBudget && customer.totalBudget > 0 ? (
                <>
                  <svg viewBox="0 0 36 36" className="circular-chart">
                    <path
                      className="circle-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="circle"
                      strokeDasharray={`${Math.min((customer.totalPaid / customer.totalBudget) * 100, 100)}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="progress-text">
                    <span className="percentage">
                      {customer.totalBudget > 0 
                        ? Math.round((customer.totalPaid / customer.totalBudget) * 100) 
                        : 0}%
                    </span>
                    <span className="label">Paid</span>
                  </div>
                </>
              ) : (
                <div className="no-data">No budget set</div>
              )}
            </div>
            <div className="progress-details">
              <div className="detail-item">
                <span className="detail-label">Total Budget</span>
                <span className="detail-value">{formatCurrency(customer.totalBudget || 0)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Paid</span>
                <span className="detail-value">{formatCurrency(customer.totalPaid || 0)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Pending</span>
                <span className="detail-value">{formatCurrency(customer.totalPending || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            Projects ({projects.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            Payments ({payments.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'projects' && (
            <div className="projects-tab">
              {projects.length === 0 ? (
                <div className="no-data">No projects found for this customer</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Budget</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.id}</td>
                        <td>{project.name}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(project.status)}`}>
                            {project.status}
                          </span>
                        </td>
                        <td>{new Date(project.startDate).toLocaleDateString()}</td>
                        <td>{project.endDate ? new Date(project.endDate).toLocaleDateString() : '-'}</td>
                        <td>{formatCurrency(project.budget)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="payments-tab">
              {payments.length === 0 ? (
                <div className="no-data">No payments found for this customer</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Amount</th>
                      <th>Payment Date</th>
                      <th>Payment Method</th>
                      <th>Transaction Ref</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{payment.id}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                        <td>{payment.paymentMethod || '-'}</td>
                        <td>{payment.transactionRef || '-'}</td>
                        <td>{payment.notes || '-'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setSelectedItem(payment)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="documents-tab">
              <DocumentManager 
                entityType="CUSTOMER" 
                entityId={id}
                canUpload={true}
                canDelete={true}
              />
            </div>
          )}
        </div>
      </div>




      {selectedItem && (
        <div className="ic-modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="ic-modal-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <h2>Payment Details</h2>
              <button className="ic-modal-close" onClick={() => setSelectedItem(null)}>×</button>
            </div>
            
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Transaction ID</span>
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>#{selectedItem.id}</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Amount</span>
                <span style={{ fontSize: '16px', color: '#16a34a', fontWeight: '700' }}>
                  {formatCurrency(selectedItem.amount)}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Date</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>
                  {new Date(selectedItem.paymentDate).toLocaleDateString()}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Payment Method</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.paymentMethod || 'CASH'}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Reference ID / No</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.transactionRef || '-'}</span>
              </div>

              <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Notes / Description</span>
                <span style={{ fontSize: '14px', color: '#4b5563', fontStyle: selectedItem.notes ? 'normal' : 'italic' }}>
                  {selectedItem.notes || 'No notes provided'}
                </span>
              </div>
            </div>
            
            <div className="ic-modal-actions" style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button type="button" className="ic-btn-secondary" onClick={() => setSelectedItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDetail;
